import { FC, useState, ReactElement } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Typography, Paper, Tabs, Tab, Collapse } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import PaymentIcon from '@material-ui/icons/Payment';
import useStore from '@hooks/useStore';
import { PAYMENT_LINK_PAYMENT_METHODS, Partner } from '@constants/paymentLink';
import { useStyles } from './styles';
import FormPartnerLogo from '@components/FormPartnerLogo';

// Payment form components
import StripeCardForm from '@containers/checkout/payment-form/CardPaymentForm/StripeCardForm';
import OpenPayCardForm from '@containers/checkout/payment-form/CardPaymentForm/OpenPayCardForm';
import DLocalCardForm from '@containers/checkout/payment-form/CardPaymentForm/DLocalCardForm';
import KushkiCardForm from '@containers/checkout/payment-form/CardPaymentForm/KushkiCardForm';
import FiservCardForm from '@containers/checkout/payment-form/CardPaymentForm/FiservCardForm';
import BlumonCardForm from '@containers/checkout/payment-form/CardPaymentForm/BlumonCardForm';
import RedPayCardForm from '@containers/checkout/payment-form/CardPaymentForm/RedPayCardForm';
import SpeiPaymentForm from '@containers/checkout/payment-form/SpeiPaymentForm';
import CashPaymentForm from '@containers/checkout/payment-form/CashPaymentForm';
import DirectDebitPaymentForm from '@containers/checkout/payment-form/DirectDebitPaymentForm';
import PayeeInfoForm from '@containers/checkout/payment-form/PayeeInfoForm';
import PayerCardAddressForm from '@containers/checkout/payment-form/PayerCardAddressForm';

// Card brand icons
import visaIcon from '@assets/images/partners/visa.png';
import amexIcon from '@assets/images/partners/amex.png';
import mastercardIcon from '@assets/images/partners/masterCard.png';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const getMethodIcon = (method: PAYMENT_LINK_PAYMENT_METHODS): ReactElement => {
  switch (method) {
    case PAYMENT_LINK_PAYMENT_METHODS.CARD:
      return <CreditCardIcon />;
    case PAYMENT_LINK_PAYMENT_METHODS.SPEI:
      return <AccountBalanceIcon />;
    case PAYMENT_LINK_PAYMENT_METHODS.CASH:
      return <LocalAtmIcon />;
    case PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT:
      return <AccountBalanceIcon />;
    default:
      return <PaymentIcon />;
  }
};

const getMethodTranslationKey = (method: PAYMENT_LINK_PAYMENT_METHODS): string => {
  switch (method) {
    case PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT:
      return 'directDebit';
    default:
      return method.toLowerCase();
  }
};

const getCardForm = (partner: Partner, apiKey: string) => {
  switch (partner) {
    case Partner.OPENPAY:
      return <OpenPayCardForm apiKey={apiKey} />;
    case Partner.DLOCAL:
      return <DLocalCardForm apiKey={apiKey} />;
    case Partner.FISERV:
      return <FiservCardForm apiKey={apiKey} />;
    case Partner.BLUMON:
      return <BlumonCardForm apiKey={apiKey} />;
    case Partner.REDPAY:
      return <RedPayCardForm apiKey={apiKey} />;
    case Partner.STRIPE:
      return <StripeCardForm apiKey={apiKey} />;
    case Partner.KUSHKI:
    default:
      return <KushkiCardForm apiKey={apiKey} />;
  }
};

const PaymentForm: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { links, checkout } = useStore();
  const [selectedTab, setSelectedTab] = useState(0);
  const [infoOpen, setInfoOpen] = useState(true);
  const [addressOpen, setAddressOpen] = useState(true);
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      payeeFirstName: '',
      payeeLastName: '',
      payeeEmail: '',
      payeePhone: '',
      payerAddress: '',
      payerCity: '',
      payerState: '',
      payerCountry: '',
      payerZipCode: '',
    },
  });

  const { isOnlyCardFormRequired, isOnlyDirectDebitFormRequired, isSubscription } =
    links.paymentLink;
  const { card } = links.paymentConfig.paymentMethods;

  const handleTabChange = (_: unknown, newValue: number) => {
    setSelectedTab(newValue);
  };

  const getPaymentForm = (method: PAYMENT_LINK_PAYMENT_METHODS) => {
    const paymentMethods = links.paymentConfig.paymentMethods;

    switch (method) {
      case PAYMENT_LINK_PAYMENT_METHODS.CARD:
        if (!paymentMethods.card?.enabled) return null;
        return (
          <>
            {card?.isAddressRequired && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {t('checkoutScreen.payment.payerAddress.title')}
                </Typography>
                <Paper elevation={0} className={classes.paper}>
                  <div className={classes.formContent}>
                    <PayerCardAddressForm />
                  </div>
                </Paper>
              </>
            )}
            <Typography variant="h6" className={`${classes.sectionTitle} ${classes.cardTitle}`}>
              {t('checkoutScreen.payment.methods.card.title')}
            </Typography>
            <div className={classes.brands}>
              <img src={visaIcon} alt="Visa" className="brand visa" />
              <img src={mastercardIcon} alt="Mastercard" className="brand mastercard" />
              <img src={amexIcon} alt="American Express" className="brand amex" />
            </div>
            {getCardForm(paymentMethods.card.partner as Partner, paymentMethods.card.publicApiKey)}
          </>
        );
      case PAYMENT_LINK_PAYMENT_METHODS.SPEI:
        return paymentMethods.spei?.enabled ? (
          <SpeiPaymentForm
            onValidationError={(error) => {
              console.error('SPEI validation error:', error);
            }}
          />
        ) : null;
      case PAYMENT_LINK_PAYMENT_METHODS.CASH:
        return paymentMethods.cash?.enabled ? (
          <CashPaymentForm
            onValidationError={(error) => {
              console.error('Cash validation error:', error);
            }}
          />
        ) : null;
      case PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT:
        return paymentMethods.directDebit?.enabled ? (
          <DirectDebitPaymentForm
            onValidationError={(error) => {
              console.error('DirectDebit validation error:', error);
            }}
          />
        ) : null;
      default:
        return null;
    }
  };

  const getMethodEnabled = (method: PAYMENT_LINK_PAYMENT_METHODS) => {
    if (isOnlyCardFormRequired) {
      return method === PAYMENT_LINK_PAYMENT_METHODS.CARD;
    }
    if (isOnlyDirectDebitFormRequired) {
      return method === PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT;
    }

    // Si es una suscripción, solo permitir tarjeta y débito directo
    if (isSubscription) {
      switch (method) {
        case PAYMENT_LINK_PAYMENT_METHODS.CARD:
          return (
            links.paymentConfig.paymentMethods.card?.enabled && checkout.isAllowedToPayWithCard
          );
        case PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT:
          return (
            links.paymentConfig.paymentMethods.directDebit?.enabled &&
            checkout.isAllowedToPayWithDirectDebit
          );
        default:
          return false;
      }
    }

    // Si no es una suscripción, mostrar todos los métodos habilitados
    switch (method) {
      case PAYMENT_LINK_PAYMENT_METHODS.CARD:
        return links.paymentConfig.paymentMethods.card?.enabled && checkout.isAllowedToPayWithCard;
      case PAYMENT_LINK_PAYMENT_METHODS.SPEI:
        return links.paymentConfig.paymentMethods.spei?.enabled && checkout.isAllowedToPayWithSpei;
      case PAYMENT_LINK_PAYMENT_METHODS.CASH:
        return links.paymentConfig.paymentMethods.cash?.enabled && checkout.isAllowedToPayWithCash;
      case PAYMENT_LINK_PAYMENT_METHODS.DIRECT_DEBIT:
        return (
          links.paymentConfig.paymentMethods.directDebit?.enabled &&
          checkout.isAllowedToPayWithDirectDebit
        );
      default:
        return false;
    }
  };

  const availablePaymentMethods = Object.values(PAYMENT_LINK_PAYMENT_METHODS).filter(
    getMethodEnabled,
  );

  return (
    <FormProvider {...methods}>
      <form className={classes.root}>
        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('checkoutScreen.payment.info.title')}
          </Typography>
          <Collapse in={infoOpen} timeout="auto">
            <Paper elevation={0} className={classes.paper}>
              <div className={classes.formContent}>
                <PayeeInfoForm />
              </div>
            </Paper>
          </Collapse>
        </div>

        <div className={classes.section}>
          {!isOnlyDirectDebitFormRequired && !isOnlyCardFormRequired && (
            <Typography variant="h6" className={classes.sectionTitle}>
              {t('checkoutScreen.payment.methods.title')}
            </Typography>
          )}
          <Paper elevation={0} className={classes.paper}>
            {!isOnlyDirectDebitFormRequired && !isOnlyCardFormRequired ? (
              <>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  TabIndicatorProps={{ hidden: true }}
                  className={classes.tabs}
                >
                  {availablePaymentMethods.map((method) => (
                    <Tab
                      key={method}
                      label={t(
                        `checkoutScreen.payment.methods.${getMethodTranslationKey(method)}.title`,
                      )}
                      className={classes.tab}
                      icon={getMethodIcon(method)}
                    />
                  ))}
                </Tabs>
                {availablePaymentMethods.map((method, index) => (
                  <TabPanel key={method} value={selectedTab} index={index}>
                    <div className={classes.formContainer}>{getPaymentForm(method)}</div>
                  </TabPanel>
                ))}
              </>
            ) : (
              <div className={classes.formContainer}>
                {getPaymentForm(availablePaymentMethods[0])}
              </div>
            )}
          </Paper>
        </div>
      </form>
    </FormProvider>
  );
};

export default observer(PaymentForm);
